/**
 * CountrySelect — a country picker that renders a flag next to the
 * country name.
 *
 * Design choice: this component wraps the existing native `<select>`
 * (see `src/components/ui/select.tsx`), NOT a Radix popover. The
 * project's `select.tsx` explicitly prefers the native element so
 * `userEvent.selectOptions` keeps working across all routes, the OS
 * picker stays in charge on mobile, and there is no behavioral diff.
 *
 * Flag rendering: prefers `flag_base64` (inline data URI from API) for
 * instant display with zero extra HTTP requests. Falls back to `flag_url`
 * (file URL) for environments where flags haven't been downloaded yet.
 */
import * as React from 'react'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'
import type { Country } from '@/lib/api'

export interface CountrySelectProps {
  id?: string
  value: number | ''
  onChange: (value: number | '') => void
  countries: Country[]
  disabled?: boolean
  className?: string
  includeEmptyOption?: boolean
  emptyLabel?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

/** Resolve the best flag source: base64 > file URL > nothing. */
function flagSrc(c: Country | null): string | null {
  if (!c) return null
  return c.flag_base64 || c.flag_url || null
}

/** Inline style for option elements — renders a 16x12 flag background. */
function optionFlagStyle(c: Country): React.CSSProperties | undefined {
  const src = flagSrc(c)
  if (!src) return undefined
  return {
    backgroundImage: `url(${src})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left 8px center',
    backgroundSize: '16px 12px',
    paddingLeft: '28px',
  }
}

const CHIP_CLASS =
  'inline-block h-5 aspect-[4/3] shrink-0 rounded-sm bg-muted/60 ring-1 ring-border'

export const CountrySelect = React.forwardRef<HTMLSelectElement, CountrySelectProps>(
  function CountrySelect(
    {
      id,
      value,
      onChange,
      countries,
      disabled,
      className,
      includeEmptyOption = true,
      emptyLabel = 'Select a country',
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    }: CountrySelectProps,
    ref,
  ) {
    const selected = countries.find((c) => c.country_id === value) || null
    const src = flagSrc(selected)

    return (
      <div className={cn('flex items-center gap-2', className)}>
        {src ? (
          <img
            src={src}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            className={CHIP_CLASS}
          />
        ) : (
          <span aria-hidden="true" className={CHIP_CLASS} />
        )}

        <Select
          ref={ref}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
          disabled={disabled}
          className="flex-1 min-w-0"
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
        >
          {includeEmptyOption && <option value="">{emptyLabel}</option>}
          {countries.map((c) => (
            <option key={c.country_id} value={c.country_id} style={optionFlagStyle(c)}>
              {c.country}
            </option>
          ))}
        </Select>
      </div>
    )
  },
)
